import { useCallback, useMemo, useState } from 'react';

import { useOptionalMultiSelect } from '@/features/content/editor/ContentEditModeProvider';
import type { SectionSelectionState } from './sectionTypes';

const SECTION_SELECTION_SCOPE = 'content-sections';
const emptySelectedSectionIds = new Set<string>();

export function useSectionSelection() {
  const multiSelect = useOptionalMultiSelect();
  const clearMultiSelectSelection = multiSelect?.clearSelection;
  const enterMultiSelectSelection = multiSelect?.enterSelection;
  const isMultiSelectSelectionActiveForScope =
    multiSelect?.isSelectionActiveForScope;
  const multiSelectScope = multiSelect?.selectMultipleScope;
  const multiSelectSelectedCount = multiSelect?.selectedCount ?? 0;
  const multiSelectSelectedRowIds = multiSelect?.selectedRowIds;
  const toggleMultiSelectRowSelection = multiSelect?.toggleRowSelection;
  const [state, setState] = useState<SectionSelectionState>(() => ({
    active: false,
    selectedSectionIds: new Set<string>(),
  }));

  const selectedCount = state.selectedSectionIds.size;

  const enterSelection = useCallback((initialSectionId: string) => {
    setState({
      active: true,
      selectedSectionIds: new Set([initialSectionId]),
    });
  }, []);

  const cancelSelection = useCallback(() => {
    setState({
      active: false,
      selectedSectionIds: new Set<string>(),
    });
  }, []);

  const toggleSection = useCallback((sectionId: string) => {
    setState((currentState) => {
      if (!currentState.active) {
        return {
          active: true,
          selectedSectionIds: new Set([sectionId]),
        };
      }

      const nextSelectedIds = new Set(currentState.selectedSectionIds);

      if (nextSelectedIds.has(sectionId)) {
        nextSelectedIds.delete(sectionId);
      } else {
        nextSelectedIds.add(sectionId);
      }

      return {
        active: true,
        selectedSectionIds: nextSelectedIds,
      };
    });
  }, []);

  const selectSection = useCallback((sectionId: string) => {
    setState((currentState) => ({
      active: true,
      selectedSectionIds: new Set(currentState.selectedSectionIds).add(sectionId),
    }));
  }, []);

  const selectedSectionIds = useMemo(
    () => Array.from(state.selectedSectionIds),
    [state.selectedSectionIds],
  );

  const contextSelectedSectionIdsSet =
    multiSelectScope === SECTION_SELECTION_SCOPE && multiSelectSelectedRowIds
      ? multiSelectSelectedRowIds
      : emptySelectedSectionIds;
  const contextSelectedSectionIds = useMemo(
    () => Array.from(contextSelectedSectionIdsSet),
    [contextSelectedSectionIdsSet],
  );
  const contextCancelSelection = useCallback(() => {
    clearMultiSelectSelection?.(SECTION_SELECTION_SCOPE);
  }, [clearMultiSelectSelection]);
  const contextEnterSelection = useCallback(
    (initialSectionId: string) => {
      enterMultiSelectSelection?.(SECTION_SELECTION_SCOPE, initialSectionId);
    },
    [enterMultiSelectSelection],
  );
  const contextToggleSection = useCallback(
    (sectionId: string) => {
      toggleMultiSelectRowSelection?.(SECTION_SELECTION_SCOPE, sectionId);
    },
    [toggleMultiSelectRowSelection],
  );
  const contextSelectSection = useCallback(
    (sectionId: string) => {
      if (!contextSelectedSectionIdsSet.has(sectionId)) {
        toggleMultiSelectRowSelection?.(SECTION_SELECTION_SCOPE, sectionId);
      }
    },
    [contextSelectedSectionIdsSet, toggleMultiSelectRowSelection],
  );

  if (multiSelect) {
    const isSelectionActive =
      Boolean(isMultiSelectSelectionActiveForScope?.(SECTION_SELECTION_SCOPE));

    return {
      cancelSelection: contextCancelSelection,
      enterSelection: contextEnterSelection,
      isSelectionActive,
      selectSection: contextSelectSection,
      selectedCount: isSelectionActive
        ? multiSelectSelectedCount
        : 0,
      selectedSectionIds: isSelectionActive
        ? contextSelectedSectionIds
        : [],
      selectedSectionIdsSet: contextSelectedSectionIdsSet,
      state: {
        active: isSelectionActive,
        selectedSectionIds: contextSelectedSectionIdsSet,
      },
      toggleSection: contextToggleSection,
    };
  }

  return {
    cancelSelection,
    enterSelection,
    isSelectionActive: state.active,
    selectSection,
    selectedCount,
    selectedSectionIds,
    selectedSectionIdsSet: state.selectedSectionIds,
    state,
    toggleSection,
  };
}
